import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const AccessibilityPage = () => {
  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-12 bg-brand-dark overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20clean%20minimal%20background%20texture%20light%20pattern%20professional%20website%20legal%20accessibility%20statement&width=1920&height=300&seq=access1&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="flex items-center justify-center gap-2 text-white/50 text-sm font-body mb-4">
              <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">Accessibility</span>
            </div>
            <h1 className="font-heading font-black text-white text-4xl md:text-5xl mb-3">Accessibility Statement</h1>
            <p className="font-body text-white/60 text-sm">Last updated: April 6, 2026</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="prose max-w-none font-body text-gray-600 text-sm leading-relaxed space-y-8">

              <div>
                <h2 className="font-heading font-bold text-brand-dark text-xl mb-3">Our Commitment</h2>
                <p>
                  Adry &amp; Mich Cleaning and Handyman Service is committed to ensuring digital accessibility for people with disabilities. We continually improve the user experience for everyone and apply relevant accessibility standards to ensure our website is accessible to all visitors, regardless of ability or technology.
                </p>
              </div>

              <div>
                <h2 className="font-heading font-bold text-brand-dark text-xl mb-3">Conformance Status</h2>
                <p>
                  We aim to conform to the <strong className="text-brand-dark">Web Content Accessibility Guidelines (WCAG) 2.1</strong> at Level AA. These guidelines explain how to make web content more accessible to people with disabilities. Conformance with these guidelines helps make the web more user-friendly for everyone.
                </p>
              </div>

              <div>
                <h2 className="font-heading font-bold text-brand-dark text-xl mb-3">Accessibility Features</h2>
                <p className="mb-3">Our website includes the following accessibility features:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong className="text-brand-dark">Keyboard Navigation:</strong> All interactive elements on our website can be accessed and operated using a keyboard alone.</li>
                  <li><strong className="text-brand-dark">Screen Reader Compatibility:</strong> Our website is designed to be compatible with screen readers and other assistive technologies.</li>
                  <li><strong className="text-brand-dark">Text Alternatives:</strong> All images include descriptive alt text to convey meaning to users who cannot see them.</li>
                  <li><strong className="text-brand-dark">Color Contrast:</strong> We maintain sufficient color contrast ratios between text and background to ensure readability for users with visual impairments.</li>
                  <li><strong className="text-brand-dark">Resizable Text:</strong> Text on our website can be resized up to 200% without loss of content or functionality.</li>
                  <li><strong className="text-brand-dark">Descriptive Links:</strong> Links are written to be descriptive and meaningful out of context.</li>
                  <li><strong className="text-brand-dark">Consistent Navigation:</strong> Navigation menus are consistent across all pages to help users orient themselves.</li>
                  <li><strong className="text-brand-dark">Language Identification:</strong> The language of each page is identified in the code to support screen readers.</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading font-bold text-brand-dark text-xl mb-3">Known Limitations</h2>
                <p>
                  While we strive to ensure the highest level of accessibility, some third-party content embedded on our website (such as Google Maps and review widgets) may not fully conform to WCAG 2.1 Level AA. We are working with our third-party providers to improve accessibility where possible.
                </p>
              </div>

              <div>
                <h2 className="font-heading font-bold text-brand-dark text-xl mb-3">Assistive Technologies Supported</h2>
                <p className="mb-3">Our website is designed to be compatible with the following assistive technologies:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Screen readers (NVDA, JAWS, VoiceOver, TalkBack)</li>
                  <li>Voice recognition software (Dragon NaturallySpeaking)</li>
                  <li>Screen magnification software</li>
                  <li>Keyboard-only navigation</li>
                  <li>Switch access devices</li>
                </ul>
              </div>

              <div>
                <h2 className="font-heading font-bold text-brand-dark text-xl mb-3">Feedback and Contact</h2>
                <p className="mb-3">
                  We welcome your feedback on the accessibility of our website. If you experience any barriers or have suggestions for improvement, please contact us. We aim to respond to accessibility feedback within 2 business days.
                </p>
                <div className="bg-brand-beige rounded-2xl p-5 flex flex-col gap-2">
                  <p className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-mail-line text-brand-green"></i>
                    <a href="mailto:adrimich7778@hotmail.com" className="hover:text-brand-green transition-colors cursor-pointer">adrimich7778@hotmail.com</a>
                  </p>
                  <p className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-phone-line text-brand-green"></i>
                    <a href="tel:+15612626118" className="hover:text-brand-green transition-colors cursor-pointer">(561) 262-6118</a>
                  </p>
                  <p className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-whatsapp-line text-brand-green"></i>
                    <a href="https://wa.me/15612626118" target="_blank" rel="noopener noreferrer" className="hover:text-brand-green transition-colors cursor-pointer">WhatsApp: (561) 262-6118</a>
                  </p>
                  <p className="flex items-center gap-2 text-sm text-gray-600">
                    <i className="ri-map-pin-line text-brand-green"></i>
                    10639 Misty Ln, Royal Palm Beach, FL 33411
                  </p>
                </div>
              </div>

              <div>
                <h2 className="font-heading font-bold text-brand-dark text-xl mb-3">Formal Complaints</h2>
                <p>
                  If you are not satisfied with our response to your accessibility concern, you may contact the relevant enforcement authority in your jurisdiction. In the United States, you may file a complaint with the U.S. Department of Justice Civil Rights Division or the U.S. Access Board.
                </p>
              </div>

              <div>
                <h2 className="font-heading font-bold text-brand-dark text-xl mb-3">Updates to This Statement</h2>
                <p>
                  This accessibility statement was last reviewed and updated on April 6, 2026. We review and update this statement regularly as we continue to improve the accessibility of our website.
                </p>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default AccessibilityPage;
